import React, { useEffect, useState } from 'react';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import AdminLayout from '../AdminLayout';
import startupService from '../../services/startupService';

export default function StartupsPage() {
    const navigate = useNavigate();
    const { userProfile } = useAuth();

    const [startups, setStartups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: '' });

    useEffect(() => {
        if (userProfile?.role !== 'admin') {
            navigate('/');
            return;
        }
        fetchStartups();
    }, [userProfile, navigate]);

    const fetchStartups = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await startupService.getStartups();
            setStartups(data);
        } catch (err) {
            console.error('Error fetching startups:', err);
            setError(err.message || 'Failed to load startups');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            setLoading(true);
            await startupService.deleteStartup(deleteDialog.id);
            setStartups(startups.filter(s => s.id !== deleteDialog.id));
            setDeleteDialog({ open: false, id: null, name: '' });
            setError('');
        } catch (err) {
            console.error('Error deleting startup:', err);
            setError(err.message || 'Failed to delete startup');
        } finally {
            setLoading(false);
        }
    };

    const filteredStartups = startups.filter(startup =>
        startup.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        startup.founder?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        startup.category?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getStatusColor = (status) => {
        const colors = {
            'Active': 'bg-green-100 text-green-800',
            'Inactive': 'bg-gray-100 text-gray-800',
            'Graduated': 'bg-blue-100 text-blue-800',
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const getStageColor = (stage) => {
        const colors = {
            'Idea': 'bg-yellow-100 text-yellow-800',
            'MVP': 'bg-blue-100 text-blue-800',
            'Growth': 'bg-green-100 text-green-800',
            'Scale': 'bg-purple-100 text-purple-800',
        };
        return colors[stage] || 'bg-gray-100 text-gray-800';
    };

    return (
        <AdminLayout title="Startup Management">
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-4xl font-bold text-gray-800">🚀 Startup Management</h1>
                        <button
                            onClick={() => navigate('/startups/new')}
                            className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all shadow-lg"
                        >
                            <AddIcon /> Add Startup
                        </button>
                    </div>

                    {/* Error Alert */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex justify-between items-center">
                            <span>{error}</span>
                            <button onClick={() => setError('')} className="text-red-700 font-bold">×</button>
                        </div>
                    )}

                    {/* Search Bar */}
                    <div className="mb-6">
                        <input
                            type="text"
                            placeholder="Search by name, founder, or category..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-md"
                        />
                    </div>

                    {/* Table */}
                    <div className="bg-white rounded-lg shadow-xl overflow-hidden">
                        {loading ? (
                            <div className="flex justify-center items-center py-12">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                            </div>
                        ) : filteredStartups.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                                <p className="text-lg">
                                    {searchTerm ? 'No startups match your search' : 'No startups found. Create one to get started!'}
                                </p>
                            </div>
                        ) : (
                            <table className="w-full">
                                <thead className="bg-gray-100 border-b-2 border-gray-300">
                                    <tr>
                                        <th className="px-6 py-4 text-left font-semibold text-gray-700">Name</th>
                                        <th className="px-6 py-4 text-left font-semibold text-gray-700">Founder</th>
                                        <th className="px-6 py-4 text-left font-semibold text-gray-700">Category</th>
                                        <th className="px-6 py-4 text-left font-semibold text-gray-700">Stage</th>
                                        <th className="px-6 py-4 text-left font-semibold text-gray-700">Status</th>
                                        <th className="px-6 py-4 text-center font-semibold text-gray-700">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredStartups.map((startup, idx) => (
                                        <tr key={startup.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} border-b hover:bg-blue-50 transition`}>
                                            <td className="px-6 py-4 font-medium text-gray-900">{startup.name}</td>
                                            <td className="px-6 py-4 text-gray-700">{startup.founder || '-'}</td>
                                            <td className="px-6 py-4 text-gray-700">{startup.category || '-'}</td>
                                            <td className="px-6 py-4">
                                                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStageColor(startup.stage)}`}>
                                                    {startup.stage}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(startup.status)}`}>
                                                    {startup.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <div className="flex justify-center gap-3">
                                                    <button
                                                        onClick={() => navigate(`/startups/${startup.id}`)}
                                                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition"
                                                        title="View"
                                                    >
                                                        <ViewIcon className="text-xl" />
                                                    </button>
                                                    <button
                                                        onClick={() => navigate(`/startups/${startup.id}`)}
                                                        className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition"
                                                        title="Edit"
                                                    >
                                                        <EditIcon className="text-xl" />
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteDialog({ open: true, id: startup.id, name: startup.name })}
                                                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition"
                                                        title="Delete"
                                                    >
                                                        <DeleteIcon className="text-xl" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Delete Dialog */}
                    {deleteDialog.open && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                            <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full">
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">Delete Startup?</h2>
                                <p className="text-gray-700 mb-6">
                                    Are you sure you want to delete <strong>{deleteDialog.name}</strong>? This action cannot be undone.
                                </p>
                                <div className="flex gap-4">
                                    <button
                                        onClick={() => setDeleteDialog({ open: false, id: null, name: '' })}
                                        className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition font-semibold"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleDelete}
                                        disabled={loading}
                                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold disabled:opacity-50"
                                    >
                                        {loading ? 'Deleting...' : 'Delete'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
}
